/**
 * Helpers PKCE / OIDC para o UCA-SSO
 * A flag Enabled vem da API (SSO_ENABLED) em runtime — não usa VUE_APP_SSO_ENABLED.
 */

function base64UrlEncode(buffer) {
  const bytes = buffer instanceof ArrayBuffer ? new Uint8Array(buffer) : buffer
  let str = ''
  bytes.forEach((b) => { str += String.fromCharCode(b) })
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function randomString(length = 64) {
  const array = new Uint8Array(length)
  crypto.getRandomValues(array)
  return base64UrlEncode(array)
}

async function sha256(plain) {
  const data = new TextEncoder().encode(plain)
  return await crypto.subtle.digest('SHA-256', data)
}

export async function createPkce() {
  const verifier = randomString(64)
  const challenge = base64UrlEncode(await sha256(verifier))
  const nonce = randomString(32)
  const state = randomString(32)
  return { verifier, challenge, nonce, state }
}

export function buildAuthorizeUrl({
  authorizeUrl,
  clientId,
  redirectUri,
  codeChallenge,
  state,
  nonce,
  environment,
  theme,
  locale = 'pt-BR',
  darkMode = false,
}) {
  const url = new URL(assertSafeSsoUrl(authorizeUrl, { kind: 'https' }))
  url.searchParams.set('response_type', 'code')
  url.searchParams.set('client_id', clientId)
  url.searchParams.set('redirect_uri', assertSafeSsoUrl(redirectUri, {
    kind: redirectUri && redirectUri.startsWith('uca.lms.app:') ? 'native' : 'https',
  }))
  url.searchParams.set('scope', 'openid profile email')
  url.searchParams.set('code_challenge', codeChallenge)
  url.searchParams.set('code_challenge_method', 'S256')
  if (state) url.searchParams.set('state', state)
  if (nonce) url.searchParams.set('nonce', nonce)
  if (environment) url.searchParams.set('environment', environment)
  if (theme) url.searchParams.set('theme', theme)
  url.searchParams.set('locale', locale || 'pt-BR')
  url.searchParams.set('darkMode', darkMode === true ? 'true' : 'false')
  return url.toString()
}

const CONFIG_STORAGE_KEY = 'uca_sso_config'
const SSO_NATIVE_SCHEME = 'uca.lms.app:'
const SSO_ALLOWED_HOSTS = new Set([
  'uca-sso-api.mangoplant-adc32715.brazilsouth.azurecontainerapps.io',
  'uca-sso-hml.azurewebsites.net',
  'uca-sso-prd.azurewebsites.net',
  'uca-lms-hml-app.azurewebsites.net',
  'uca-lms-hml-api.azurewebsites.net',
  'uca-lms-prd-app.azurewebsites.net',
  'uca-lms-prd-api.azurewebsites.net',
  'ava.uca.edu.local',
  'aluno.uca.edu.br',
])

let cachedConfig = null
let loadPromise = null

function isNativePlatform() {
  return typeof window !== 'undefined'
    && window.Capacitor
    && window.Capacitor.isNativePlatform
    && window.Capacitor.isNativePlatform()
}

function isDevRuntime() {
  return process.env.NODE_ENV === 'development'
}

function isLoopbackHost(hostname) {
  return hostname === 'localhost'
    || hostname === '127.0.0.1'
    || hostname === '10.0.2.2'
}

function extraAllowedHostPatterns() {
  const raw = process.env.VUE_APP_SSO_ALLOWED_HOSTS || ''
  return raw.split(',').map((item) => item.trim().toLowerCase()).filter(Boolean)
}

function hostMatchesAllowedPattern(host, pattern) {
  if (pattern.startsWith('*.')) {
    const suffix = pattern.slice(1)
    return suffix.length > 1 && host.endsWith(suffix) && host.length > suffix.length
  }
  return host === pattern
}

function isAllowedSsoHost(hostname) {
  const host = (hostname || '').toLowerCase()
  if (!host) return false
  if (SSO_ALLOWED_HOSTS.has(host)) return true
  if (extraAllowedHostPatterns().some((pattern) => hostMatchesAllowedPattern(host, pattern)))
    return true
  if (host.endsWith('.uca.edu.local')) return true
  if (host.endsWith('.uca.edu.br')) return true
  return false
}

export function assertSafeSsoUrl(raw, { kind = 'https' } = {}) {
  if (!raw || typeof raw !== 'string')
    throw new Error('SSO URL inválida')

  let url
  try {
    url = new URL(raw)
  } catch (e) {
    throw new Error('SSO URL inválida')
  }

  if (kind === 'native') {
    if (url.protocol !== SSO_NATIVE_SCHEME)
      throw new Error('SSO redirect nativo deve usar uca.lms.app')
    return url.toString()
  }

  const host = url.hostname.toLowerCase()
  const loopback = isLoopbackHost(host)

  if (url.protocol === 'http:') {
    if (!isDevRuntime() || !loopback)
      throw new Error('SSO URL deve ser HTTPS')
    return url.toString()
  }

  if (url.protocol !== 'https:')
    throw new Error('SSO URL deve ser HTTPS')

  // dev aceita qualquer host HTTPS (issuer local/efémero); hml e prod usam a allowlist
  if (loopback || isDevRuntime())
    return url.toString()

  if (!isAllowedSsoHost(host))
    throw new Error('SSO host não permitido')

  return url.toString()
}

function normalizeConfig(data) {
  if (!data || !data.Enabled) {
    return { Enabled: false }
  }

  const redirectUri = isNativePlatform()
    ? assertSafeSsoUrl(data.RedirectUriNative || 'uca.lms.app://sso/callback', { kind: 'native' })
    : assertSafeSsoUrl(data.RedirectUri || `${window.location.origin}/sso/callback`, { kind: 'https' })

  return {
    Enabled: true,
    authorizeUrl: assertSafeSsoUrl(data.AuthorizeUrl, { kind: 'https' }),
    clientId: data.ClientId,
    environment: data.Environment,
    logoutUrl: data.LogoutUrl
      ? assertSafeSsoUrl(data.LogoutUrl, { kind: 'https' })
      : '',
    redirectUri,
    redirectUriNative: data.RedirectUriNative
      ? assertSafeSsoUrl(data.RedirectUriNative, { kind: 'native' })
      : '',
    isNative: isNativePlatform(),
  }
}

function persistConfig(config) {
  cachedConfig = config
  try {
    sessionStorage.setItem(CONFIG_STORAGE_KEY, JSON.stringify(config))
  } catch (e) {
    // sessionStorage indisponível
  }
}

function readPersistedConfig() {
  try {
    const raw = sessionStorage.getItem(CONFIG_STORAGE_KEY)
    if (!raw) return null
    return JSON.parse(raw)
  } catch (e) {
    return null
  }
}

/**
 * Consulta GET /auth/sso-config. Em falha assume SSO desligado (login local).
 */
export async function loadSsoConfig(force = false) {
  if (!force && cachedConfig)
    return cachedConfig

  if (!force && loadPromise)
    return loadPromise

  loadPromise = (async () => {
    try {
      // import dinâmico evita dependência circular no bundle
      const { default: authService } = await import('../Services/Auth.service')
      const result = await authService.getSsoConfig()
      if (!result.Success)
        throw new Error(result.Message || 'Falha ao obter sso-config')

      const config = normalizeConfig(result.Data)
      persistConfig(config)
      return config
    } catch (e) {
      console.warn('[SSO] configuração rejeitada — a usar login local:', e?.message || e)
      const fallback = { Enabled: false }
      persistConfig(fallback)
      return fallback
    } finally {
      loadPromise = null
    }
  })()

  return loadPromise
}

export function isSsoEnabled() {
  return !!getSsoConfig().Enabled
}

export function getSsoUiPreferences() {
  return {
    locale: window.localStorage.getItem('app-locale') || 'pt-BR',
    darkMode: window.localStorage.getItem('darkModeEnabled') === 'true',
  }
}

function sanitizeStoredConfig(config) {
  if (!config?.Enabled)
    return { Enabled: false }
  try {
    return normalizeConfig({
      Enabled: true,
      AuthorizeUrl: config.authorizeUrl,
      ClientId: config.clientId,
      Environment: config.environment,
      LogoutUrl: config.logoutUrl,
      RedirectUri: config.isNative ? undefined : config.redirectUri,
      RedirectUriNative: config.redirectUriNative || (config.isNative ? config.redirectUri : undefined),
    })
  } catch (e) {
    return { Enabled: false }
  }
}

export function getSsoConfig() {
  if (cachedConfig?.Enabled) {
    const safe = sanitizeStoredConfig(cachedConfig)
    if (safe.Enabled) return safe
  }

  const persisted = readPersistedConfig()
  if (persisted?.Enabled) {
    const safe = sanitizeStoredConfig(persisted)
    if (safe.Enabled) return safe
  }

  return {
    Enabled: false,
    authorizeUrl: '',
    clientId: '',
    environment: '',
    logoutUrl: '',
    redirectUri: `${typeof window !== 'undefined' ? window.location.origin : ''}/sso/callback`,
    isNative: isNativePlatform(),
  }
}

export function getSsoLogoutUrl(postLogoutRedirectUri) {
  const cfg = getSsoConfig()
  if (!cfg.Enabled || !cfg.logoutUrl)
    return null

  const url = new URL(assertSafeSsoUrl(cfg.logoutUrl, { kind: 'https' }))
  if (postLogoutRedirectUri)
    url.searchParams.set('post_logout_redirect_uri', assertSafeSsoUrl(postLogoutRedirectUri, { kind: 'https' }))
  return url.toString()
}

const PKCE_STORAGE_KEY = 'uca_sso_pkce'

export function savePkceSession(data) {
  sessionStorage.setItem(PKCE_STORAGE_KEY, JSON.stringify(data))
}

export function loadPkceSession() {
  const raw = sessionStorage.getItem(PKCE_STORAGE_KEY)
  if (!raw) return null
  try {
    return JSON.parse(raw)
  } catch (e) {
    return null
  }
}

export function clearPkceSession() {
  sessionStorage.removeItem(PKCE_STORAGE_KEY)
}

export default {
  createPkce,
  assertSafeSsoUrl,
  buildAuthorizeUrl,
  loadSsoConfig,
  isSsoEnabled,
  getSsoUiPreferences,
  getSsoConfig,
  getSsoLogoutUrl,
  savePkceSession,
  loadPkceSession,
  clearPkceSession,
}
